import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  MessageCircle, Send, StickyNote, ChevronLeft, AlertCircle, RefreshCw,
  Smile, Paperclip, X, Image, FileText, Music, Video, File,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import api from "../../services/api";
import LoadingSpinner from "../LoadingSpinner";
import { toast } from "react-toastify";
import MessageBubble from "./MessageBubble";
import NoteBubble from "./NoteBubble";
import InfoPanel from "./InfoPanel";
import TemplatePicker from "./TemplatePicker";
import DateSeparator from "./DateSeparator";
import { formatDateSeparator, getDateKey } from "./chatUtils";
import { getSocket } from "../../services/socket";
import { getUserInfoFromToken } from "../../services/auth";

// Accepted MIME types by category
const ACCEPT_IMAGE    = "image/jpeg,image/png,image/webp,image/gif";
const ACCEPT_VIDEO    = "video/mp4,video/3gpp,video/quicktime";
const ACCEPT_AUDIO    = "audio/mpeg,audio/ogg,audio/wav,audio/mp4,audio/aac";
const ACCEPT_DOCUMENT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip";

const AttachMenu = ({ onSelect, onClose }) => {
  const items = [
    { label: "Image",    icon: Image,    type: "image",    accept: ACCEPT_IMAGE },
    { label: "Video",    icon: Video,    type: "video",    accept: ACCEPT_VIDEO },
    { label: "Audio",    icon: Music,    type: "audio",    accept: ACCEPT_AUDIO },
    { label: "Document", icon: FileText, type: "document", accept: ACCEPT_DOCUMENT },
  ];
  return (
    <div className="absolute bottom-14 left-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20 w-40">
      {items.map(({ label, icon: Icon, type, accept }) => (
        <button
          key={type}
          onClick={() => onSelect(accept)}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          {label}
        </button>
      ))}
    </div>
  );
};

const ChatThread = ({ conv, onBack, onStatusChange, onContactSaved }) => {
  const { role } = getUserInfoFromToken() || {};
  const isAgent = role === "agent";

  const [messages, setMessages]     = useState([]);
  const [notes, setNotes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [text, setText]             = useState("");
  const [sending, setSending]       = useState(false);
  const [showInfo, setShowInfo]     = useState(false);
  const [convStatus, setConvStatus] = useState(conv.status);

  // Emoji picker
  const [showEmoji, setShowEmoji]   = useState(false);
  const emojiRef                    = useRef(null);

  // Template picker
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Attachment
  const [showAttach, setShowAttach]     = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // { file, preview, type }
  const [fileCaption, setFileCaption]   = useState("");
  const fileInputRef                    = useRef(null);
  const attachRef                       = useRef(null);

  const bottomRef    = useRef(null);
  const scrollAreaRef = useRef(null);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const el = scrollAreaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const r = await api.get(`/conversations/${conv.id}/messages?limit=100`);
      setMessages(r.data.data);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [conv.id]);

  const fetchNotes = useCallback(async () => {
    try {
      const r = await api.get(`/conversations/${conv.id}/notes`);
      setNotes(r.data.data);
    } catch {}
  }, [conv.id]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setNotes([]);
    setConvStatus(conv.status);
    fetchMessages();
    fetchNotes();
  }, [conv.id, fetchMessages, fetchNotes]);

  // Instant scroll on initial load (when loading flips to false)
  useEffect(() => {
    if (!loading) scrollToBottom("instant");
  }, [loading, scrollToBottom]);

  // Smooth scroll when new messages arrive via socket
  useEffect(() => {
    if (!loading) scrollToBottom("smooth");
  }, [messages, notes, scrollToBottom]);

  // Socket
  useEffect(() => {
    const socket = getSocket();

    const onNewMessage = ({ conversationId, message }) => {
      if (conversationId !== conv.id) return;
      setMessages((prev) => {
        if (prev.find((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
    };

    const onMessageStatus = ({ messageId, status, error }) => {
      setMessages((prev) =>
        prev.map((m) => (m.messageId === messageId ? { ...m, status, ...(error && { error }) } : m))
      );
    };

    socket.on("new_message", onNewMessage);
    socket.on("message_status", onMessageStatus);
    return () => {
      socket.off("new_message", onNewMessage);
      socket.off("message_status", onMessageStatus);
    };
  }, [conv.id]);

  // Close emoji / attach dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
      if (attachRef.current && !attachRef.current.contains(e.target)) setShowAttach(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Timeline
  const timeline = useMemo(() => {
    const msgs = messages.map((m) => ({ ...m, _kind: "message" }));
    const nts  = notes.map((n) => ({ ...n, _kind: "note", timestamp: n.createdAt }));
    const sorted = [...msgs, ...nts].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const result = [];
    let lastDateKey = null;
    for (const item of sorted) {
      const dateKey = getDateKey(item.timestamp);
      if (dateKey !== lastDateKey) {
        result.push({ _kind: "date", label: formatDateSeparator(item.timestamp), key: dateKey });
        lastDateKey = dateKey;
      }
      result.push(item);
    }
    return result;
  }, [messages, notes]);

  // ── Send ─────────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (sending) return;
    if (!selectedFile && !text.trim()) return;
    setSending(true);
    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append("media", selectedFile.file);
        if (fileCaption.trim()) formData.append("caption", fileCaption.trim());
        await api.post(`/conversations/${conv.id}/messages`, formData);
        setSelectedFile(null);
        setFileCaption("");
      } else {
        await api.post(`/conversations/${conv.id}/messages`, { text });
        setText("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setText((prev) => prev + emojiData.emoji);
    setShowEmoji(false);
  };

  const handleAttachSelect = (accept) => {
    setShowAttach(false);
    fileInputRef.current.accept = accept;
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.type.startsWith("image/") ? "image"
      : file.type.startsWith("video/") ? "video"
      : file.type.startsWith("audio/") ? "audio"
      : "document";
    const preview = type === "image" ? URL.createObjectURL(file) : null;
    setSelectedFile({ file, preview, type, name: file.name });
    e.target.value = "";
  };

  const cancelFile = () => {
    if (selectedFile?.preview) URL.revokeObjectURL(selectedFile.preview);
    setSelectedFile(null);
    setFileCaption("");
  };

  const handleStatusChange = async (status) => {
    try {
      await api.post(`/conversations/${conv.id}/status`, { status });
      setConvStatus(status);
      onStatusChange(conv.id, status);
      toast.success(`Conversation marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const name     = conv.customer?.name || conv.waId;
  const initials = name.slice(0, 2).toUpperCase();

  // Icon for file preview bar
  const FileTypeIcon = selectedFile?.type === "video" ? Video
    : selectedFile?.type === "audio" ? Music
    : selectedFile?.type === "document" ? FileText
    : File;

  return (
    <div className="flex flex-1 flex-col h-full min-w-0">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white flex-shrink-0">
        {/* Row 1 — back + avatar + name + notes icon (always visible) */}
        <div className="flex items-center gap-2 px-3 py-2.5">
          <button onClick={onBack} className="md:hidden text-gray-500 hover:text-gray-700 flex-shrink-0 p-1">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
            <p className="text-xs text-gray-400 truncate">{conv.waId}</p>
          </div>
          {/* Desktop: status select + refresh + info inline */}
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            <select
              value={convStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="open">Open</option>
              <option value="waiting">Waiting</option>
              <option value="resolved">Resolved</option>
            </select>
            <button
              onClick={() => { fetchMessages(); fetchNotes(); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {/* Notes icon always visible */}
          <button
            onClick={() => setShowInfo(!showInfo)}
            className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${showInfo ? "bg-blue-100 text-blue-600" : "hover:bg-gray-100 text-gray-500"}`}
            title="Customer info & notes"
          >
            <StickyNote className="w-4 h-4" />
          </button>
        </div>

        {/* Row 2 — mobile only: status select + refresh */}
        <div className="flex md:hidden items-center gap-2 px-3 pb-2">
          <select
            value={convStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="open">Open</option>
            <option value="waiting">Waiting</option>
            <option value="resolved">Resolved</option>
          </select>
          <button
            onClick={() => { fetchMessages(); fetchNotes(); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors flex-shrink-0"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex flex-1 flex-col min-w-0">
          {/* 24h window banner */}
          {!conv.canSendFreeForm && (
            <div className="bg-orange-50 border-b border-orange-200 px-4 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <p className="text-xs text-orange-700">
                The 24-hour messaging window has expired. You can only send template messages.
              </p>
            </div>
          )}

          {/* Timeline */}
          <div ref={scrollAreaRef} className="flex-1 overflow-y-auto p-4 bg-gray-100">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <LoadingSpinner size="md" text="Loading messages..." />
              </div>
            ) : timeline.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageCircle className="w-12 h-12 mb-2 opacity-30" />
                <p className="text-sm">No messages yet</p>
              </div>
            ) : (
              <>
                {timeline.map((item) => {
                  if (item._kind === "date")    return <DateSeparator key={item.key} label={item.label} />;
                  if (item._kind === "note")    return <NoteBubble key={`note-${item.id}`} note={item} />;
                  return <MessageBubble key={`msg-${item.id}`} msg={item} />;
                })}
                <div ref={bottomRef} />
              </>
            )}
          </div>

          {/* Message input */}
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0">
            {conv.canSendFreeForm ? (
              <div className="flex flex-col gap-2">

                {/* File preview bar */}
                {selectedFile && (
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                    {selectedFile.preview ? (
                      <img src={selectedFile.preview} alt="preview" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <FileTypeIcon className="w-5 h-5 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{selectedFile.name}</p>
                      <input
                        value={fileCaption}
                        onChange={(e) => setFileCaption(e.target.value)}
                        placeholder="Add a caption…"
                        className="text-xs text-gray-500 bg-transparent outline-none w-full mt-0.5 placeholder-gray-400"
                      />
                    </div>
                    <button onClick={cancelFile} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Input row */}
                <div className="flex items-end gap-2 relative">
                  {/* Emoji */}
                  <div ref={emojiRef} className="relative flex-shrink-0">
                    <button
                      onClick={() => { setShowEmoji((v) => !v); setShowAttach(false); }}
                      className={`p-2 rounded-lg transition-colors ${showEmoji ? "bg-yellow-100 text-yellow-500" : "hover:bg-gray-100 text-gray-400"}`}
                      title="Emoji"
                    >
                      <Smile className="w-5 h-5" />
                    </button>
                    {showEmoji && (
                      <div className="absolute bottom-11 left-0 z-30">
                        <EmojiPicker
                          onEmojiClick={handleEmojiClick}
                          height={380}
                          width={320}
                          previewConfig={{ showPreview: false }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Attachment */}
                  <div ref={attachRef} className="relative flex-shrink-0">
                    <button
                      onClick={() => { setShowAttach((v) => !v); setShowEmoji(false); }}
                      className={`p-2 rounded-lg transition-colors ${showAttach ? "bg-blue-100 text-blue-500" : "hover:bg-gray-100 text-gray-400"}`}
                      title="Attach file"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    {showAttach && (
                      <AttachMenu onSelect={handleAttachSelect} onClose={() => setShowAttach(false)} />
                    )}
                  </div>

                  {/* Text area */}
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={selectedFile ? "Caption already added above…" : "Type a message… (Enter to send)"}
                    disabled={!!selectedFile}
                    rows={2}
                    className="flex-1 text-sm border border-gray-300 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-50 disabled:text-gray-400"
                  />

                  {/* Send */}
                  <button
                    onClick={handleSend}
                    disabled={sending || (!text.trim() && !selectedFile)}
                    className="w-10 h-10 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>

                {/* Hidden file input */}
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
              </div>
            ) : (
              <div className="flex items-center justify-center py-2 gap-3">
                <p className="text-sm text-gray-500">24-hour window expired</p>
                {!isAgent && (
                  <button
                    className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    onClick={() => setShowTemplatePicker(true)}
                  >
                    Send Template
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info panel */}
        {showInfo && (
          <InfoPanel
            conv={conv}
            notes={notes}
            onNoteAdded={(note) => setNotes((prev) => [...prev, note])}
            onNoteUpdated={(updated) => setNotes((prev) => prev.map((n) => n.id === updated.id ? updated : n))}
            onNoteDeleted={(noteId) => setNotes((prev) => prev.filter((n) => n.id !== noteId))}
            onClose={() => setShowInfo(false)}
            onContactSaved={(customer) => onContactSaved?.(conv.id, customer)}
          />
        )}
      </div>

      {/* Template picker modal */}
      {showTemplatePicker && (
        <TemplatePicker
          convId={conv.id}
          onClose={() => setShowTemplatePicker(false)}
          onSent={fetchMessages}
        />
      )}
    </div>
  );
};

export default ChatThread;
